import React, { useState } from "react"; // <-- added useState
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import downloadIcon from "../../assets/VideoDownloader/downloadicon.png";
import "./VideoDownloader.css";

import Footer from "../../components/Footer/Footer";
import ReadyToStartBox from "../../components/ReadyToStartBox/ReadyToStartBox";
import Navbar from "../../components/Navbar/Navbar";
import DashboardSidebar from "../../components/DashboardSidebar/DashboardSidebar";
import useAuthStore from "../../stores/authStore";

const API_URL = process.env.REACT_APP_API_URL;

export default function VideoDownloader() {
  const { user } = useAuthStore();
  const location = useLocation();

  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [videoSrc, setVideoSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDashboardLayout = location.pathname.startsWith("/dashboard");

  const handleDownload = async (e) => {
  e.preventDefault();
  if (!linkedinUrl.trim()) return;

  setLoading(true);
  setError(null);
  setVideoSrc(null);

  let attempt = 0;
  let success = false;

  while (attempt < 2 && !success) {
    try {
      const res = await fetch(`${API_URL}/api/public/video-dn`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: linkedinUrl }),
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to fetch video");

      const data = await res.json();

      if (data?.formats?.length) {
        const mp4Formats = data.formats.filter((f) => f.ext === "mp4");
        const bestFormat =
          mp4Formats.find((f) => f.format_id.includes("720")) || mp4Formats[0];

        setVideoSrc(bestFormat.url);
        success = true;
      } else {
        throw new Error("No video formats found.");
      }
    } catch (err) {
      attempt++;
      if (attempt >= 2) {
        console.error(err);
        setError("Servers are busy, please try again later.");
      }
    }
  }

  setLoading(false);
};


  const handleDirectDownload = async () => {
    try {
      const res = await fetch(videoSrc);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "linkedin-video.mp4";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  return (
    <>
      {isDashboardLayout ? (
        <DashboardSidebar
          userName={user?.name}
          profilePic={user?.profilePic}
        />
      ) : (
        <Navbar />
      )}

      <Helmet>
        <title>Download LinkedIn Videos Free | Fast & Easy Online Tool</title>
        <meta
          name="description"
          content="Download LinkedIn videos instantly for free. Paste your LinkedIn video link and get high-quality downloads with no sign-up required."
        />
        <meta
          name="keywords"
          content="LinkedIn video downloader, download LinkedIn videos, free LinkedIn video tool, save LinkedIn videos"
        />
        <meta property="og:title" content="Download LinkedIn Videos Free" />
        <meta
          property="og:description"
          content="Free online tool to download LinkedIn videos instantly. Perfect for creators, marketers, and professionals."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://yourwebsite.com/linkedin-video-downloader"
        />
        <meta
          property="og:image"
          content="https://yourwebsite.com/og-image-linkedin-downloader.jpg"
        />
        <link
          rel="canonical"
          href="https://yourwebsite.com/linkedin-video-downloader"
        />
      </Helmet>

      <header className="vd-hero">
        <div className="vd-icon">
          <img src={downloadIcon} alt="Download icon" className="vd-icon-img" />
        </div>
        <h1>
          <span className="vd-highlight">Download</span> LinkedIn Videos
        </h1>
        <p className="vd-subtitle">
          Copy and paste your URL and download videos for free.
        </p>
        <form
          className="vd-form"
          aria-label="LinkedIn Video Downloader"
          onSubmit={handleDownload}
        >
          <input
            type="url"
            id="linkedin-url"
            name="linkedin-url"
            placeholder="LinkedIn Post URL"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            required
          />
          <button type="submit" className="vd-download-btn" disabled={loading}>
            {loading ? "Loading..." : "Download"}
          </button>
        </form>
      </header>

      {/* Video preview section */}
      {videoSrc && (
        <section className="vd-video-preview">
          <div className="vd-video-card">
            <video src={videoSrc} controls />
            <button
              onClick={handleDirectDownload}
              className="vd-video-download-btn"
            >
              ⬇ Download Video
            </button>
          </div>
        </section>
      )}

      {error && <p className="vd-error">{error}</p>}

      <div className="vd-divider"></div>

      <section className="vd-intro">
        <h2>
          LinkedIn <span className="vd-highlight">Loves</span> Videos
        </h2>
        <p>
          In today’s fast-scrolling LinkedIn feed, video content is king. As
          attention spans shrink and users skim instead of read, short-form
          video is the most powerful way to connect with your audience.
        </p>
        <p>
          Brand.ly helps creators, marketers, and founders craft scroll-stopping
          LinkedIn videos that drive real results — from engagement to leads.
        </p>
      </section>

      <section className="vd-benefits">
        <article>
          <h3>More Eyes on You</h3>
          <p>
            Video posts get way more attention — over 300 million impressions in
            just the first year of LinkedIn launching video. That’s not a trend
            — that’s a shift.
          </p>
        </article>
        <article>
          <h3>Engagement That Text Can’t Match</h3>
          <p>
            Videos on LinkedIn get 3x more engagement than plain text. Whether
            you’re sharing insights, updates, or quick tips — a short video gets
            people to pause and interact.
          </p>
        </article>
        <article>
          <h3>Real Conversations Start Here</h3>
          <p>
            LinkedIn members are 5x more likely to start a conversation from a
            video. That means more profile views, more DMs, and more
            opportunities.
          </p>
        </article>
        <article>
          <h3>B2B’s Favorite Tool</h3>
          <p>
            85% of B2B marketers say video helps generate leads. 78% say it
            boosts brand awareness. It’s the go-to format if you’re serious
            about growth.
          </p>
        </article>
      </section>

      {!isDashboardLayout && <ReadyToStartBox />}
      <Footer />
    </>
  );
}
